'use client';

import { CommerceQueryProvider } from '@contexts/CommerceQueryProvider';
import { useCurrency } from '@contexts/CurrencyContext';
import { EnvironmentVariable, getEnvVar } from '@lib/config';
import { ACTIVE_ORGANIZATION_ID_COOKIE } from '@lib/constants/cookies';
import { commerceAxiosClient } from '@lib/http/commerceAxiosClient';
import { failingFetcher } from '@lib/http/fetch';
import { Organization } from '@lib/types/api/Organization';
import {
  CartManagementProvider,
  type CartManagementValueContext,
} from '@unity/commerce-core';
import Cookies from 'js-cookie';
import { useSession } from 'next-auth/react';
import { useParams } from 'next/navigation';
import { PropsWithChildren, useMemo } from 'react';
import useSWR from 'swr';

const STORE = 'asset';

type Environment = CartManagementValueContext['env'];

const KNOWN_ENVIRONMENTS: ReadonlySet<Environment> = new Set<Environment>([
  'development',
  'test',
  'staging',
  'production',
]);

const resolveEnv = (): Environment => {
  const value = getEnvVar(EnvironmentVariable.nextEnvironment);
  return KNOWN_ENVIRONMENTS.has(value as Environment)
    ? (value as Environment)
    : 'staging';
};

type CommerceCartProviderProps = PropsWithChildren;

export const CommerceCartProvider = ({
  children,
}: CommerceCartProviderProps) => {
  const { currency } = useCurrency();
  const { data: session } = useSession();
  const params = useParams<{ locale: string }>();
  const genesisUser = session?.accessToken?.genesis_user;
  const userId = typeof genesisUser === 'string' ? genesisUser : undefined;

  // OrganizationsProvider only wraps the header's UserMenu, so re-fetch here.
  const { data: orgsData } = useSWR<{
    organizations: Organization[];
    defaultOrganizationId?: string;
  }>(session?.accessToken ? '/api/users/organizations' : null, failingFetcher);

  // Org switching triggers a hard navigation, so the cookie is stable per mount.
  const activeOrganization = useMemo(() => {
    const organizations = orgsData?.organizations ?? [];
    const storedOrgId = Cookies.get(ACTIVE_ORGANIZATION_ID_COOKIE);
    return (
      organizations.find((org) => org.id === storedOrgId) ??
      organizations.find((org) => org.id === orgsData?.defaultOrganizationId) ??
      organizations[0]
    );
  }, [orgsData]);

  const initialValues: Omit<
    CartManagementValueContext,
    'locale' | 'organizationId' | 'userId'
  > = {
    store: STORE,
    client: commerceAxiosClient,
    currencyCode: currency,
    // commerce-core gates cart fetch on a non-empty countryCode; derive
    // from locale (e.g. 'en-US' → 'US') so cart-bound actions resolve.
    countryCode: params?.locale?.split('-')[1]?.toUpperCase() || 'US',
    env: resolveEnv(),
    // Start false so no cart is created on PDP load; the hook flips this on
    // only when the user clicks Buy Now.
    enableCartFetching: false,
    ensureCreateAuthenticatedCart: true,
    enableAnonymousCart: false,
    // Re-price the cart in the active currency on fetch, so a footer currency
    // switch is reflected before checkout.
    syncCartCurrency: true,
  };

  return (
    <CommerceQueryProvider>
      <CartManagementProvider
        locale={params?.locale}
        userId={userId}
        organizationId={activeOrganization?.id}
        initialValues={initialValues}
      >
        {children}
      </CartManagementProvider>
    </CommerceQueryProvider>
  );
};
